<template>
    <div class="datepicker" ref="datepicker" @focusout="onBlur" tabindex="0">
        <!-- Need to wrap together with input to properly dismiss when user interacts with input / better multiple datepickers switch -->
        <!-- Readonly is important to have. Readonly prevents from being taken into account upon validation. That's why it enables temporary only when calendar is shown-->
        <Dismissible :show.sync="showCalendar">
            <div class="datepicker__input-wrapper" @click.prevent="showCalendar = true">
                <slot name="pre"> </slot>

                <input
                    type="text"
                    :id="inputId"
                    v-bind="{ value, placeholder, required }"
                    ref="input"
                    :value="value"
                    :placeholder="placeholder"
                    :aria-label="placeholder"
                    inputmode="none"
                    @focus="onFocus"
                    @keydown="onKeyDown"
                    :readonly="showCalendar"
                    class="form-control input-gray datepicker__input"
                />
                <span v-if="showDuration" class="datepicker__input-duration">
                    ({{ $t('Generic.until') }} {{ checkoutDate }})
                </span>
            </div>

            <i
                v-if="value && allowClear"
                class="datepicker__icon remove-icon"
                @click.prevent="change(null)"
                title="Entfernen"
            >
                <IconSvg filepath="close.svg" />
            </i>

            <i v-else class="datepicker__icon calendar-icon" @click.prevent="togglePicker">
                <IconSvg filepath="calendar.svg" />
            </i>

            <Calendar
                v-if="showCalendar"
                :available-dates="availableDates"
                :selected="value"
                @update:selected="change"
                @click.native.prevent="() => {}"
            >
                <div class="d-flex justify-content-between px-2 py-2">
                    <button v-if="allowClear" class="btn btn-sm btn-danger" @click="change('')">
                        {{ $t('SearchBar.dropdown.button.reset') }}
                    </button>
                    <button class="btn btn-secondary btn-sm" @click="showCalendar = false">
                        {{ $t('Generic.close') }}
                    </button>
                </div>
            </Calendar>
        </Dismissible>
    </div>
</template>

<script>
import Calendar from '@/components/product/Calendar';
import Dismissible from '@/components/Dismissible';

export default {
    name: 'Datepicker',
    components: {
        Calendar,
        Dismissible
    },
    data() {
        return {
            showCalendar: false
        };
    },
    props: {
        value: {
            type: String,
            required: true
        },
        placeholder: {
            type: String,
            required: true
        },
        required: Boolean,
        availableDates: {
            type: Array,
            required: true
        },
        allowClear: Boolean,
        duration: Number,
        inputId: String
    },
    computed: {
        checkoutDate() {
            const [dd, mm, yyyy] = (this.value || '').split('.');
            const result = new Date(`${yyyy}-${mm}-${dd}`);
            result.setDate(result.getDate() + this.duration);
            return new Intl.DateTimeFormat('de-DE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            }).format(result);
        },
        showDuration() {
            return this.duration != null && this.value;
        }
    },
    methods: {
        togglePicker() {
            this.showCalendar = !this.showCalendar;
        },
        change(date) {
            this.$emit('change', date);
            this.showCalendar = false;
        },
        onKeyDown(e) {
            const keycode = e.which || e.keyCode;
            const isTabKey = keycode === 9;

            if (isTabKey) {
                return true;
            }

            e.preventDefault();
            return false;
        },
        onFocus(e) {
            this.showCalendar = true;

            /**
             * Yeah, extremely unreliable and nasty solution, but Safari iOS does not support inputmethod property and shows virtual keyboard that takes precious space and overlaps calendar dropdwon
             */
            document.activeElement.blur();

            return true;
        },
        onBlur(e) {
            if (e.relatedTarget && e.relatedTarget.closest('.datepicker') !== this.$refs.datepicker) {
                this.showCalendar = false;
            }
            return true;
        }
    }
};
</script>

<style scoped lang="scss">
@use '@/styles/variables';

.datepicker {
    position: relative;

    &__input-wrapper {
        position: relative;
    }

    &__input {
        background-color: var(--inputs-bg);
        position: relative;
        z-index: 2;
        caret-color: transparent;
    }

    &__input-duration {
        position: absolute;
        font-size: 12px;
        top: 50%;
        z-index: 2;
        transform: translateY(-50%);
        left: 90px;
        opacity: 0.4;
    }

    input[type='date'] {
        position: absolute;
        pointer-events: none;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        z-index: 1;
    }

    &__icon {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        z-index: 3;
        cursor: pointer;
        color: var(--body-color-disabled);

        &:hover {
            color: var(--body-color);
        }

        &.calendar-icon,
        &.remove-icon {
            right: variables.$grid-padding;
        }

        &.calendar-icon {
            height: 26px;
        }
    }

    ::v-deep .calendar__table {
        background: var(--white);
        position: absolute;
        top: 100%;
        left: 0;
        width: 100%;
        margin: 0;
        z-index: 100;
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2);
        min-width: 260px;
    }
}
</style>
